<template>
  <div class="home">
    <header class="header">
      <div class="container">
        <div class="header-content">
          <div class="logo-container">
            <img src="@/assets/logo.png" alt="Forms Pro for Google Forms logo" class="logo-img" />
            <h1 class="logo">Forms Pro for Google Forms</h1>
          </div>
          <nav class="nav">
            <router-link to="/" class="nav-link">Home</router-link>
            <a href="/privacy" class="nav-link">Privacy</a>
            <a href="/terms" class="nav-link">Terms</a>
            <router-link to="/subscription" class="nav-link">Subscription</router-link>
          </nav>
        </div>
      </div>
    </header>

    <main class="main">
      <!-- Hero Section -->
      <section class="hero" aria-labelledby="hero-title">
        <div class="container">
          <div class="hero-content">
            <div class="hero-text">
              <span class="badge">Independent mobile productivity app</span>
              <h1 id="hero-title" class="hero-title">Forms Pro for Google Forms</h1>
              <p class="hero-subtitle">
                Create, edit, and manage Google Forms from your mobile device. Build forms with AI-assisted drafting, monitor responses, and work with related Google Drive and Google Sheets files.
              </p>

              <div id="data-use" class="oauth-summary">
                <strong class="oauth-summary-title">How we use Google data</strong>
                <p>
                  After you authorize access through Google OAuth, the app uses only the Google Forms, Google Drive files, and Google Sheets data needed for features you choose. Google user data is not sold, used for advertising, or used to train AI models.
                </p>
                <a href="/privacy#google-data">Read our Google user data policy</a>
              </div>
              
              <div class="download-buttons">
                <a
                  href="https://apps.apple.com/us/app/forms-app-ai-form-manager/id6756334475"
                  class="download-btn apple"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download Forms Pro for Google Forms on the App Store"
                >
                  <svg class="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <div class="btn-text">
                    <span class="btn-label">Download on the</span>
                    <span class="btn-title">App Store</span>
                  </div>
                </a>
              </div>

              <div class="data-principles" aria-label="Google data principles">
                <span>User-authorized access</span>
                <span>Purpose-limited use</span>
                <span>User-controlled revocation</span>
              </div>
            </div>
            
            <div class="hero-image">
              <div class="phone-mockup">
                <div class="phone-screen">
                  <div class="mock-content">
                    <!-- Status Bar -->
                    <div class="mock-status-bar">
                      <span class="status-time">9:41</span>
                      <div class="status-icons">
                        <span class="status-icon">📶</span>
                        <span class="status-icon">📶</span>
                        <span class="status-icon">🔋</span>
                      </div>
                    </div>
                    
                    <!-- Form Header -->
                    <div class="mock-form-header">
                      <div class="mock-avatar"></div>
                      <div class="mock-header-text">
                        <div class="mock-title-line"></div>
                        <div class="mock-subtitle-line"></div>
                      </div>
                    </div>
                    
                    <!-- Form Content -->
                    <div class="mock-form-container">
                      <div class="mock-section-title"></div>
                      
                      <!-- Form Fields -->
                      <div class="mock-form-field">
                        <div class="mock-label"></div>
                        <div class="mock-input active"></div>
                      </div>
                      
                      <div class="mock-form-field">
                        <div class="mock-label"></div>
                        <div class="mock-input"></div>
                      </div>
                      
                      <div class="mock-form-field">
                        <div class="mock-label"></div>
                        <div class="mock-input-select">
                          <div class="select-content"></div>
                          <span class="select-arrow">▼</span>
                        </div>
                      </div>
                      
                      <div class="mock-checkbox-group">
                        <div class="mock-checkbox checked"></div>
                        <div class="mock-checkbox-label"></div>
                      </div>
                      
                      <!-- Submit Button -->
                      <div class="mock-submit-btn">
                        <div class="btn-shine"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Features Section -->
      <section id="features" class="features">
        <div class="container">
          <div class="section-header">
            <span class="section-badge">Features</span>
            <h2 class="section-title">Work with Google Forms from anywhere</h2>
            <p class="section-subtitle">Mobile tools for user-initiated form creation, editing, sharing, and response monitoring</p>
          </div>

          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon-wrapper blue">
                <span class="feature-emoji">✨</span>
              </div>
              <h3 class="feature-title">Mobile Form Creation</h3>
              <p class="feature-description">Create and edit Google Forms from a mobile device with familiar form controls</p>
            </div>

            <div class="feature-card">
              <div class="feature-icon-wrapper purple">
                <span class="feature-emoji">📊</span>
              </div>
              <h3 class="feature-title">Response Monitoring</h3>
              <p class="feature-description">Review form responses and related reporting data when you choose to sync them</p>
            </div>

            <div class="feature-card">
              <div class="feature-icon-wrapper green">
                <span class="feature-emoji">🔗</span>
              </div>
              <h3 class="feature-title">Google Drive Workflow</h3>
              <p class="feature-description">Open Forms and related files that you select or own in Google Drive</p>
            </div>

            <div class="feature-card">
              <div class="feature-icon-wrapper orange">
                <span class="feature-emoji">🎨</span>
              </div>
              <h3 class="feature-title">AI-Assisted Drafting</h3>
              <p class="feature-description">Draft form content from your direct instructions without using Google user data for model training</p>
            </div>

            <div class="feature-card">
              <div class="feature-icon-wrapper pink">
                <span class="feature-emoji">🔒</span>
              </div>
              <h3 class="feature-title">Secure OAuth Access</h3>
              <p class="feature-description">Connect your account through Google OAuth and revoke access from your Google Account at any time</p>
            </div>

            <div class="feature-card">
              <div class="feature-icon-wrapper cyan">
                <span class="feature-emoji">📱</span>
              </div>
              <h3 class="feature-title">Custom Form Design</h3>
              <p class="feature-description">Organize questions, sections, themes, images, and validation from your phone</p>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <section class="cta-section">
        <div class="container">
          <div class="cta-content">
            <h2 class="cta-title">Your Google data stays under your control</h2>
            <p class="cta-subtitle">We access Google user data only with your permission and only to provide the form features you request.</p>
            <div class="cta-buttons">
              <a href="/privacy#google-data" class="cta-btn primary">Read Privacy Policy</a>
              <a href="/terms" class="cta-btn secondary">View Terms</a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <div class="footer-logo-container">
              <img src="@/assets/logo.png" alt="Forms Pro for Google Forms logo" class="footer-logo-img" />
              <h3 class="footer-logo">Forms Pro for Google Forms</h3>
            </div>
            <p class="footer-desc">Create, edit, and manage Google Forms from your mobile device.</p>
          </div>
          <div class="footer-section">
            <h4>Product</h4>
            <ul class="footer-links">
              <li><a href="#features">Features</a></li>
              <li><a href="#data-use">Google Data Use</a></li>
              <li><router-link to="/subscription">Subscription</router-link></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>Legal</h4>
            <ul class="footer-links">
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
              <li><router-link to="/subscription">Subscription</router-link></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>Contact</h4>
            <ul class="footer-links">
              <li><a href="mailto:sl9283191@gmail.com">sl9283191@gmail.com</a></li>
              <li><a href="mailto:sl9283191@gmail.com?subject=Forms%20Pro%20Support">Support Center</a></li>
            </ul>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2026 Forms Pro for Google Forms. All rights reserved.</p>
          <p class="trademark-note">Forms Pro for Google Forms is an independent third-party app and is not affiliated with, endorsed by, or sponsored by Google LLC. Google Forms and Google Drive are trademarks of Google LLC.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Home'
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home {
  min-height: 100vh;
  background: #ffffff;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-img {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.logo {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0;
  margin: 0;
}

.nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: #666;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  transition: color 0.3s;
  position: relative;
  padding-bottom: 2px;
}

.nav-link:hover {
  color: #1a1a1a;
}

.nav-link.router-link-active {
  color: #4c51bf;
  border-bottom: 2px solid #4c51bf;
}

/* Hero Section */
.hero {
  padding: 80px 0 60px;
  background: linear-gradient(135deg, #f8f9fc 0%, #e9ecff 50%, #dbeafe 100%);
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234c51bf' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
}

.hero-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.hero-text {
  color: #1a1a1a;
}

.badge {
  display: inline-block;
  padding: 8px 16px;
  background: rgba(76, 81, 191, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 24px;
  border: 1px solid rgba(76, 81, 191, 0.2);
  color: #4c51bf;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 20px;
  letter-spacing: 0;
}

.hero-subtitle {
  font-size: 1.25rem;
  line-height: 1.6;
  color: #4b5563;
  margin-bottom: 24px;
}

.oauth-summary {
  max-width: 620px;
  margin-bottom: 28px;
  padding: 4px 0 4px 18px;
  border-left: 3px solid #4c51bf;
  color: #374151;
}

.oauth-summary-title {
  display: block;
  margin-bottom: 6px;
  color: #1f2a44;
  font-size: 1rem;
}

.oauth-summary p {
  margin-bottom: 8px;
  font-size: 0.95rem;
  line-height: 1.65;
}

.oauth-summary a {
  color: #3846a5;
  font-size: 0.9rem;
  font-weight: 600;
  text-underline-offset: 3px;
}

.download-buttons {
  display: flex;
  gap: 16px;
  margin-bottom: 28px;
}

.download-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #4c51bf 0%, #6366f1 100%);
  border: 2px solid transparent;
  border-radius: 12px;
  color: white;
  text-decoration: none;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(76, 81, 191, 0.3);
}

.download-btn:hover {
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(76, 81, 191, 0.4);
}

.btn-icon {
  width: 32px;
  height: 32px;
}

.btn-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.btn-label {
  font-size: 0.75rem;
  opacity: 0.9;
}

.btn-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.data-principles {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
  color: #4b5563;
  font-size: 0.86rem;
  font-weight: 600;
}

.data-principles span::before {
  content: '✓';
  margin-right: 7px;
  color: #277a52;
}

/* Phone Mockup */
.hero-image {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.phone-mockup {
  width: 320px;
  height: 640px;
  background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
  border-radius: 45px;
  padding: 14px;
  box-shadow: 
    0 30px 60px rgba(0, 0, 0, 0.3),
    0 0 0 2px rgba(255, 255, 255, 0.05) inset,
    0 0 40px rgba(76, 81, 191, 0.2);
  position: relative;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.phone-mockup::before {
  content: '';
  position: absolute;
  top: 25px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 30px;
  background: #1a1a1a;
  border-radius: 0 0 25px 25px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 35px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1) inset;
}

.mock-content {
  background: #f8f9fc;
  height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Status Bar */
.mock-status-bar {
  background: white;
  padding: 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a1a1a;
  border-bottom: 1px solid #e5e7eb;
}

.status-time {
  font-weight: 700;
}

.status-icons {
  display: flex;
  gap: 4px;
}

.status-icon {
  font-size: 0.7rem;
}

/* Form Header */
.mock-form-header {
  background: white;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.mock-avatar {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4c51bf 0%, #6366f1 100%);
  border-radius: 12px;
  position: relative;
}

.mock-avatar::after {
  content: '📝';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.5rem;
}

.mock-header-text {
  flex: 1;
}

.mock-title-line {
  height: 16px;
  width: 140px;
  background: linear-gradient(90deg, #1a1a1a 0%, #4b5563 50%, #1a1a1a 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  margin-bottom: 8px;
  animation: shimmer 2s infinite;
}

.mock-subtitle-line {
  height: 12px;
  width: 100px;
  background: #e5e7eb;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Form Container */
.mock-form-container {
  flex: 1;
  padding: 24px 20px;
  overflow-y: auto;
}

.mock-section-title {
  height: 14px;
  width: 80px;
  background: #4c51bf;
  border-radius: 4px;
  margin-bottom: 20px;
}

/* Form Fields */
.mock-form-field {
  margin-bottom: 20px;
}

.mock-label {
  height: 12px;
  width: 60px;
  background: #9ca3af;
  border-radius: 3px;
  margin-bottom: 8px;
}

.mock-input {
  height: 48px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  position: relative;
  transition: all 0.3s;
}

.mock-input.active {
  border-color: #4c51bf;
  box-shadow: 0 0 0 3px rgba(76, 81, 191, 0.1);
}

.mock-input.active::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 60%;
  height: 8px;
  background: linear-gradient(90deg, #4c51bf 0%, #6366f1 100%);
  border-radius: 4px;
  animation: typing 1.5s infinite;
}

@keyframes typing {
  0%, 100% {
    width: 60%;
  }
  50% {
    width: 70%;
  }
}

.mock-input-select {
  height: 48px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
}

.select-content {
  width: 100px;
  height: 10px;
  background: #d1d5db;
  border-radius: 4px;
}

.select-arrow {
  color: #9ca3af;
  font-size: 0.75rem;
}

/* Checkbox */
.mock-checkbox-group {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
}

.mock-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #4c51bf;
  border-radius: 4px;
  position: relative;
}

.mock-checkbox.checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
}

.mock-checkbox.checked {
  background: #4c51bf;
}

.mock-checkbox-label {
  height: 12px;
  width: 120px;
  background: #d1d5db;
  border-radius: 3px;
}

/* Submit Button */
.mock-submit-btn {
  height: 52px;
  background: linear-gradient(135deg, #4c51bf 0%, #6366f1 100%);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(76, 81, 191, 0.3);
  margin-top: 8px;
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shine 2s infinite;
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.mock-submit-btn::after {
  content: 'Submit Form';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
}

/* Features Section */
.features {
  padding: 100px 0;
  background: #f8f9fc;
}

.section-header {
  text-align: center;
  max-width: 700px;
  margin: 0 auto 60px;
}

.section-badge {
  display: inline-block;
  padding: 6px 16px;
  background: #e0e7ff;
  color: #667eea;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 16px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
  letter-spacing: 0;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #666;
  line-height: 1.6;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.feature-card {
  background: white;
  padding: 32px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  border-color: #667eea;
}

.feature-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 1.75rem;
}

.feature-icon-wrapper.blue { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.feature-icon-wrapper.purple { background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%); }
.feature-icon-wrapper.green { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
.feature-icon-wrapper.orange { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.feature-icon-wrapper.pink { background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); }
.feature-icon-wrapper.cyan { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }

.feature-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.feature-description {
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
}

/* CTA Section */
.cta-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  position: relative;
  overflow: hidden;
}

.cta-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.3) 0%, transparent 70%);
  border-radius: 50%;
}

.cta-content {
  text-align: center;
  position: relative;
  z-index: 1;
}

.cta-title {
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 16px;
  letter-spacing: 0;
}

.cta-subtitle {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
}

.cta-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.cta-btn {
  padding: 16px 40px;
  font-size: 1.125rem;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s;
  display: inline-block;
}

.cta-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.cta-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.5);
}

.cta-btn.secondary {
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.cta-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

/* Footer */
.footer {
  background: #1a1a1a;
  color: #999;
  padding: 60px 0 30px;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;
}

.footer-logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.footer-logo-img {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.footer-logo {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
}

.footer-desc {
  color: #999;
  line-height: 1.6;
  font-size: 0.95rem;
}

.footer-section h4 {
  color: white;
  font-weight: 600;
  margin-bottom: 16px;
}

.footer-links {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-links a {
  color: #999;
  text-decoration: none;
  transition: color 0.3s;
  font-size: 0.95rem;
}

.footer-links a:hover {
  color: #667eea;
}

.footer-bottom {
  text-align: center;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
}

.trademark-note {
  max-width: 820px;
  margin: 12px auto 0;
  color: #777;
  font-size: 0.78rem;
  line-height: 1.55;
}

/* Responsive */
@media (max-width: 968px) {
  .hero-content {
    grid-template-columns: 1fr;
    text-align: center;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-image {
    display: none;
  }
  
  .download-buttons {
    justify-content: center;
  }
  
  .data-principles {
    justify-content: center;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .footer-content {
    grid-template-columns: 1fr;
    text-align: center;
  }
  
  .nav {
    gap: 1rem;
  }
  
  .cta-title {
    font-size: 2rem;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .logo {
    font-size: 1.25rem;
  }
  
  .logo-img {
    height: 28px;
  }
  
  .logo-container {
    gap: 8px;
  }
  
  .download-buttons {
    flex-direction: column;
  }
  
  .download-btn {
    justify-content: center;
  }

  .oauth-summary {
    padding-left: 0;
    border-left: 0;
    border-top: 3px solid #4c51bf;
    padding-top: 14px;
  }

  .data-principles {
    flex-direction: column;
  }
}
</style>
